import { matchPath } from "react-router-dom";

import type { GuideTourModuleDefinition, GuideTourResolvedModule, GuideTourStep } from "./types";

export function buildGuideTourCurrentRoute(pathname: string, search = "") {
  return search ? `${pathname}${search}` : pathname;
}

export function getGuideTourRouteLabel(
  step: Pick<GuideTourStep, "route" | "routeLabel" | "routePattern">,
  currentRoute: string,
) {
  return step.routeLabel ?? step.routePattern ?? step.route ?? currentRoute;
}

export function doesGuideTourStepMatchRoute(
  step: GuideTourStep,
  pathname: string,
  search = "",
): boolean {
  return getGuideTourRouteMatchScore(step, pathname, search) !== null;
}

function getGuideTourRouteMatchScore(
  step: GuideTourStep,
  pathname: string,
  search = "",
): number | null {
  const currentRoute = buildGuideTourCurrentRoute(pathname, search);

  if (step.route === currentRoute) {
    return 10000 + step.route.length;
  }

  if (step.route) {
    const routeUrl = new globalThis.URL(step.route, "https://guide-tour.local");
    if (routeUrl.pathname === pathname) {
      if (!routeUrl.search) {
        return 8000 + routeUrl.pathname.length;
      } else {
        const expectedSearchParams = routeUrl.searchParams;
        const currentSearchParams = new globalThis.URLSearchParams(search);
        let expectedSearchParamCount = 0;
        let isSearchMatch = true;
        for (const [key, value] of expectedSearchParams) {
          expectedSearchParamCount += 1;
          if (currentSearchParams.get(key) !== value) {
            isSearchMatch = false;
            break;
          }
        }

        if (isSearchMatch) {
          return 9000 + routeUrl.pathname.length + expectedSearchParamCount;
        }
      }
    }
  }

  if (step.routePattern && matchPath({ path: step.routePattern, end: true }, pathname)) {
    return 7000 + step.routePattern.length;
  }

  return null;
}

export function getGuideTourRouteStartStepIndex(
  steps: GuideTourStep[],
  pathname: string,
  search = "",
): number | null {
  let bestStepIndex: number | null = null;
  let bestScore = -1;

  steps.forEach((step, stepIndex) => {
    const score = getGuideTourRouteMatchScore(step, pathname, search);
    if (score === null || score <= bestScore) {
      return;
    }

    bestStepIndex = stepIndex;
    bestScore = score;
  });

  return bestStepIndex;
}

export function getGuideTourStartStepIndex(steps: GuideTourStep[], pathname: string, search = "") {
  const currentStepIndex = getGuideTourRouteStartStepIndex(steps, pathname, search);

  return currentStepIndex ?? 0;
}

export function getResolvedGuideTourModules<TModule extends string>(
  moduleDefinitions: GuideTourModuleDefinition<TModule>[],
  resolveSteps: (module: TModule) => GuideTourStep[],
): GuideTourResolvedModule<TModule>[] {
  return moduleDefinitions.flatMap((moduleDefinition) => {
    const steps = resolveSteps(moduleDefinition.key);

    if (steps.length === 0) {
      return [];
    }

    return [
      {
        ...moduleDefinition,
        steps,
      },
    ];
  });
}
